import React, { useEffect, useState } from "react"
import { Moon, Sun } from "lucide-react"
import { motion } from "framer-motion"
import { cn } from "../../lib/utils"

interface ThemeButtonProps {
  className?: string
  size?: "sm" | "md" | "lg"
}

export function CoolThemeToggle({
  className,
  size = "md",
}: ThemeButtonProps) {
  const [theme, setTheme] = useState<"light" | "dark">("light")
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)

    const saved =
      localStorage.getItem("theme") as "light" | "dark" | null

    const initial =
      saved ??
      (document.documentElement.classList.contains("dark")
        ? "dark"
        : "light")

    setTheme(initial)

    document.documentElement.classList.toggle(
      "dark",
      initial === "dark"
    )
  }, [])

  const toggleTheme = () => {
    const next = theme === "light" ? "dark" : "light"

    setTheme(next)

    document.documentElement.classList.toggle(
      "dark",
      next === "dark"
    )

    localStorage.setItem("theme", next)
  }

  const sizes = {
    sm: {
      button: 34,
      icon: 15,
    },
    md: {
      button: 40,
      icon: 17,
    },
    lg: {
      button: 46,
      icon: 20,
    },
  }

  const current = sizes[size]

  if (!mounted) {
    return (
      <div
        className={cn("rounded-full", className)}
        style={{
          width: current.button,
          height: current.button,
          background: "rgba(15,23,42,0.05)",
        }}
      />
    )
  }

  const isDark = theme === "dark"

  return (
    <motion.button
      type="button"
      onClick={toggleTheme}
      aria-label={`Switch to ${isDark ? "light" : "dark"} theme`}
      whileHover={{
        scale: 1.06,
      }}
      whileTap={{
        scale: 0.92,
      }}
      transition={{
        type: "spring",
        stiffness: 400,
        damping: 24,
      }}
      className={cn(
        "relative flex items-center justify-center rounded-full",
        "border outline-none",
        "transition-colors duration-300",
        "focus-visible:ring-2 focus-visible:ring-cyan-400/40",
        className
      )}
      style={{
        width: current.button,
        height: current.button,
        background: isDark
          ? "rgba(17,24,39,0.85)"
          : "rgba(255,255,255,0.8)",
        borderColor: isDark
          ? "rgba(125,211,252,0.14)"
          : "rgba(15,23,42,0.08)",
        boxShadow: isDark
          ? "0 4px 18px rgba(0,0,0,0.25)"
          : "0 4px 14px rgba(15,23,42,0.06)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
    >
      <motion.div
        key={theme}
        initial={{
          opacity: 0,
          scale: 0.5,
          rotate: -90,
        }}
        animate={{
          opacity: 1,
          scale: 1,
          rotate: 0,
        }}
        transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1],
        }}
      >
        {isDark ? (
          <Sun
            size={current.icon}
            strokeWidth={1.7}
            color="#7dd3fc"
          />
        ) : (
          <Moon
            size={current.icon}
            strokeWidth={1.7}
            color="#0ea5e9"
          />
        )}
      </motion.div>
    </motion.button>
  )
}
